import type { ThreeSidePathsType } from "@tosize/shapes/src/types/three";
import { useMemo } from "react";
import { BufferGeometry, Float32BufferAttribute, type Material, type Path, Vector3 } from "three";
import { RENDER_QUALITY } from "./Shape";

interface SideProps {
	side: ThreeSidePathsType;
	height: number;
	material: Material;
}

export function Side({ side, height, material }: SideProps) {
	const sideGeometry = useMemo(() => extrudePath(side.path, height), [height, side.path]);

	return (
		<group name={side.code}>
			<mesh geometry={sideGeometry} material={material} />
		</group>
	);
}

function extrudePath(path: Path, height: number) {
	const points = path.getPoints(RENDER_QUALITY);
	const vertices = [];

	for (let i = 0; i < points.length - 1; i++) {
		const startBottom = new Vector3(points[i]?.x, points[i]?.y, 0);
		const endBottom = new Vector3(points[i + 1]?.x, points[i + 1]?.y, 0);
		const startTop = startBottom.clone().add(new Vector3(0, 0, height));
		const endTop = endBottom.clone().add(new Vector3(0, 0, height));

		// Create the two triangles for the segment
		vertices.push(
			// First Triangle
			startBottom.x,
			startBottom.y,
			startBottom.z,
			endBottom.x,
			endBottom.y,
			endBottom.z,
			startTop.x,
			startTop.y,
			startTop.z,

			// Second Triangle
			startTop.x,
			startTop.y,
			startTop.z,
			endBottom.x,
			endBottom.y,
			endBottom.z,
			endTop.x,
			endTop.y,
			endTop.z,
		);
	}

	const geometry = new BufferGeometry();
	geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));
	geometry.computeVertexNormals(); // Compute the vertex normals for correct shading

	return geometry;
}
